import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { PaymentService } from './payment.service';
import { ToastrService } from 'ngx-toastr';
import * as constants from 'src/app/common/constant/constants.type'
import { IBill } from '../bill/ibill';
import { ValidationService } from 'src/app/common/validation/validation.service';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';

@Component({
  selector: 'app-payment',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule
  ],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss'
})
export class PaymentComponent implements OnInit,OnDestroy {

  paymentForm: FormGroup;
  paymentType: CommonDropDown[] = [];
  listofbillNumber: CommonDropDown[] = [];
  showAddOptionforpayment = false;
  isTransaction: boolean = false;
  billDetails: IBill;
  filteredBills: Observable<CommonDropDown[]>;
  maxDate: Date = new Date();
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private dialogRef: MatDialogRef<PaymentComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) private _data: { billId: number },
    private _formbuilder: FormBuilder,
    private _lookupService: LookupService,
    private _paymentService: PaymentService,
    private datePipe: DatePipe,
    private translationUtils: TranslationUtils,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {

    this.isTransaction = false;
    this.paymentForm = this._formbuilder.group({
      billno: [''],
      billid: [''],
      paymentdate: [new Date()],
      billAmount: [0],
      pendingAmount: [0],
      amount: ['', [Validators.required, ValidationService.decimalNumberValidator]],
      PaymentTypeId: ['', Validators.required],
      description: [''],
      TransactionNumber: ['']
    });

    // Load payment types first
    this._lookupService.getPaymentTypeLookup().pipe(takeUntil(this._unsubscribeAll)).subscribe();

    this._lookupService.paymenttypes$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((resp) => {
        this.paymentType = resp || [];
      });

    this.filteredBills = this.paymentForm.get('billno')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const val = typeof value === 'string' ? value : value?.name || '';
        return this._lookupService.getBillNumber(val);
      })
    );
    if (this._data?.billId) {
      this._lookupService.getBillNumber().subscribe(list => {
        const match = list.find(x => x.id == this._data.billId);
        if (match) {
          this.paymentForm.patchValue({ billno: match.name });
        }
      });
    }

    if (this._data?.billId) {
      this.getPendingAmount(this._data?.billId);
    }

  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onPaymentTypeSelected(id: number | string) {
    const payment = this.paymentType.find((x) => x.id === id);
    if (payment) {
      this.isTransaction = payment.childValue === 'true';

      const transactionControl = this.paymentForm.get('TransactionNumber');
      if (this.isTransaction) {
        transactionControl?.setValidators([Validators.required]);
      } else {
        transactionControl?.clearValidators();
        transactionControl?.reset();
      }
      transactionControl?.updateValueAndValidity();
      this.cdr.detectChanges();
    }
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  onSavePayment() {
    const data = this.paymentForm.getRawValue();
    if (data.paymentdate) {
      data.paymentdate = this.datePipe.transform(data.paymentdate, constants.DATE_FORMAT);
    }
    this._paymentService.createPayments(data).subscribe((response) => {
      if (response.isSuccess) {
        this.dialogRef.close(response);
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.PAYMENT')));
      }
      else {
        const allErrors = Object.values(response.errors).flat();
        this.toastr.error(allErrors.join("\n"));
      }
    });
  }

  onBillSelected(event: any) {
    const selectedBill = event.option.value;
    if (selectedBill) {
      this.paymentForm.patchValue({ billno: selectedBill.name });
      this.getPendingAmount(selectedBill.id)
    }
  }

  getPendingAmount(bill: number) {
    this._paymentService.getPendingDetails(bill).subscribe((data) => {

      if (data.result) {
        this.paymentForm.patchValue({ billAmount: data?.result?.billAmount, pendingAmount: data?.result?.pendingAmount, billid: bill, amount: data?.result?.pendingAmount });
        this.paymentForm.get('amount')?.setValidators([
          Validators.required,
          ValidationService.maxPendingValidator(data?.result?.pendingAmount),
          ValidationService.decimalNumberValidator
        ])
        this.paymentForm.get('amount')?.updateValueAndValidity()
        this.cdr.detectChanges();   
      }
    })
  }

}